import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { StarRating } from "../components/StarRating";
import { ContactTransporterDialog } from "../components/ContactTransporterDialog";
import { EmptyStateCTA } from "../components/EmptyStateCTA";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { VEHICLE_TYPES, TRANSPORT_SERVICES, COUNTRIES, SUPPORT_WHATSAPP_LINK } from "../lib/constants";
import { ShieldCheck, Truck, MapPin, Star, Search, Award } from "lucide-react";

const RATINGS = [{ v: "all", l: "Any rating" }, { v: "4", l: "4★ & up" }, { v: "4.5", l: "4.5★ & up" }];
const COMPLETED = [{ v: "all", l: "Any experience" }, { v: "1", l: "1+ jobs" }, { v: "5", l: "5+ jobs" }, { v: "10", l: "10+ jobs" }];

export default function FindTransporter() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ vehicle_type: "all", country: "all", capability: "all", min_rating: "all", min_completed: "all", service_area: "", search: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (f.vehicle_type !== "all") params.vehicle_type = f.vehicle_type;
    if (f.country !== "all") params.country = f.country;
    if (f.capability !== "all") params.capability = f.capability;
    if (f.min_rating !== "all") params.min_rating = parseFloat(f.min_rating);
    if (f.min_completed !== "all") params.min_completed = parseInt(f.min_completed);
    if (f.service_area) params.service_area = f.service_area;
    if (f.search) params.search = f.search;
    const r = await api.get("/transporters", { params });
    setList(r.data);
    setLoading(false);
  }, [f]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
          <span className="label-caps text-accent">Verified network</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Find a transporter</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Search admin-verified transporters by vehicle, area and reputation before you post a job. Contact happens securely through the platform — private details stay private.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 w-full flex-1">
        <div className="border border-border p-4 mb-8 space-y-3" data-testid="directory-filters">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Search by name or area…" className="rounded-none h-10 pl-9" data-testid="directory-search" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
              <SelectTrigger className="rounded-none h-10" data-testid="filter-vehicle"><SelectValue placeholder="Vehicle" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All vehicles</SelectItem>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.capability} onValueChange={(v) => setF({ ...f, capability: v })}>
              <SelectTrigger className="rounded-none h-10" data-testid="filter-capability"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All services</SelectItem>{TRANSPORT_SERVICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
              <SelectTrigger className="rounded-none h-10" data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All countries</SelectItem>{COUNTRIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.service_area} onChange={(e) => setF({ ...f, service_area: e.target.value })} placeholder="Service area" className="rounded-none h-10" data-testid="filter-area" />
            <Select value={f.min_rating} onValueChange={(v) => setF({ ...f, min_rating: v })}>
              <SelectTrigger className="rounded-none h-10" data-testid="filter-rating"><SelectValue /></SelectTrigger>
              <SelectContent>{RATINGS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.min_completed} onValueChange={(v) => setF({ ...f, min_completed: v })}>
              <SelectTrigger className="rounded-none h-10" data-testid="filter-completed"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPLETED.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-muted-foreground animate-pulse">Loading transporters…</div>
        ) : list.length === 0 ? (
          <EmptyStateCTA
            testid="directory-empty"
            icon={Truck}
            title="Verified transporters will appear here soon. Register your Sprinter, van or truck profile and get discovered by companies."
            subtitle="Build your reputation with ratings and completed jobs, and receive quote requests directly through the platform."
            actions={[
              { label: "Register as Transporter / Sprinter Driver", to: "/register", variant: "primary", testid: "directory-empty-register" },
              { label: "Contact WILTOM on WhatsApp", href: SUPPORT_WHATSAPP_LINK, variant: "whatsapp", testid: "directory-empty-whatsapp" },
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
            {list.map((t) => <TransporterCard key={t.transporter_id} t={t} />)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function TransporterCard({ t }) {
  return (
    <div className={`border p-5 flex flex-col ${t.featured ? "border-accent border-2" : "border-border"}`} data-testid={`directory-card-${t.transporter_id}`}>
      <div className="flex items-start justify-between gap-2">
        <Truck className="h-5 w-5 text-primary" />
        <div className="flex gap-1.5">
          {t.featured && <Badge className="rounded-none bg-accent text-foreground text-[9px] uppercase tracking-wider" data-testid={`featured-${t.transporter_id}`}><Award className="h-3 w-3 mr-0.5" /> Featured</Badge>}
          {t.documents_verified && <Badge className="rounded-none bg-primary text-white text-[9px] uppercase tracking-wider"><ShieldCheck className="h-3 w-3 mr-0.5" /> Verified</Badge>}
        </div>
      </div>
      <Link to={`/transporter/${t.transporter_id}`} className="mt-3">
        <h3 className="font-display font-bold text-lg tracking-tight hover:text-primary">{t.company_name || t.name}</h3>
      </Link>
      <div className="flex items-center gap-2 mt-1" data-testid={`card-rating-${t.transporter_id}`}>
        {t.average_rating != null ? (
          <><StarRating value={t.average_rating} size="h-4 w-4" /><span className="text-sm font-medium">{t.average_rating}</span><span className="text-xs text-muted-foreground">· {t.reviews_count} reviews</span></>
        ) : <span className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3.5 w-3.5" /> No reviews yet</span>}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{t.completed_jobs} completed jobs</div>

      <div className="space-y-1 mt-3 text-sm flex-1">
        {t.vehicle_type && <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {t.vehicle_type}</div>}
        {(t.service_areas || t.country) && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {[t.service_areas, t.country].filter(Boolean).join(" · ")}</div>}
        {t.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{t.bio}</p>}
        {t.job_capabilities?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {t.job_capabilities.slice(0, 4).map((c) => <span key={c} className="text-[10px] uppercase tracking-wider bg-muted px-1.5 py-0.5">{c}</span>)}
          </div>
        )}
      </div>

      <div className="mt-4">
        <ContactTransporterDialog transporterId={t.transporter_id} transporterName={t.company_name || t.name} />
      </div>
    </div>
  );
}
